import { Alert, Box, CircularProgress } from '@mui/material'
import { FlexibleBookingControl } from 'Pages/FlexibleBooking/FlexibleBookingControl'
import MiniBooking from 'Pages/MiniBooking/MiniBooking'
import Status from 'Pages/MiniBooking/Status'
import OfflineBookingPage from 'Pages/OfflineBookingPage/OfflineBookingPage'
import { useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import ReactGA from 'react-ga4'
import {
  Redirect,
  Route,
  Switch,
  matchPath,
  useLocation,
} from 'react-router-dom'
import { isPublicBookingPath } from 'helpers/authFlow'
import useAnalyticsIdentity from '../analytics/useAnalyticsIdentity'
import useCurrentTheme from '../app/utils/useCurrentTheme'
import useProfileComplete, {
  ALLOWED_INCOMPLETE_PROFILE_PATHS,
} from '../hooks/useProfileComplete'
import { Admin } from '../layouts/Admin/Admin'
import SignIn from '../layouts/Auth/Auth'
import LogoutRedirect from '../layouts/Logout/Logout'
import DirectSSO from '../layouts/SSO/DirectSSO'
import PrivateRoute from './PrivateRoute'
import PublicRoute from './PublicRoute'
import useAuth from './utils/useAuth'

//NOTE: the header will not be in place as the homepage will not have the same header as the app.  This is just used for testing.

function usePageViews() {
  const location = useLocation()

  useEffect(() => {
    const page = `${location?.pathname || ''}${location?.search || ''}`
    if (!page) return
    ReactGA.send({ hitType: 'pageview', page })
  }, [location])
}

const AppRoutes = () => {
  const location = useLocation()
  const theme = useCurrentTheme(window.location.hostname)
  const { isLoading, status, error } = useAuth(
    `${location?.pathname || ''}${location?.search || ''}`,
  )
  usePageViews()
  useAnalyticsIdentity()
  const { user, isComplete } = useProfileComplete()

  const isAuthRoute =
    location.pathname === '/sso' ||
    location.pathname.startsWith('/auth') ||
    location.pathname.startsWith('/logout')

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="var(--app-vh)"
      >
        <CircularProgress color="primary" />
      </Box>
    )
  }

  const debugInfo = {
    isMobile,
    userAgent: navigator.userAgent,
    pathname: location.pathname,
    hostname: window.location.hostname,
  }
  ;(window as any).__appRoutesDebug = debugInfo
  console.log('[AppRoutes] mobile redirect check', debugInfo)

  if (
    isMobile &&
    !matchPath(location.pathname, { path: '/booking/status/:id' }) &&
    !matchPath(location.pathname, { path: '/booking/flexible/:id' }) &&
    !location.pathname.startsWith('/offline-booking') &&
    location.pathname !== '/auth/completeRegistration' &&
    !location.pathname.startsWith('/auth') &&
    !location.pathname.startsWith('/logout') &&
    location.pathname !== '/sso'
  ) {
    const mobileUrl = theme.brand.mobileUrl ?? process.env.REACT_APP_MOBILE_URL
    return (
      <Route
        path="/"
        component={() => {
          window.location.href = mobileUrl as string
          return null
        }}
      />
    )
  }

  const onProfileGateExemptPath =
    isAuthRoute ||
    location.pathname.startsWith('/offline-booking') ||
    isPublicBookingPath(location.pathname) ||
    ALLOWED_INCOMPLETE_PROFILE_PATHS.some((path) =>
      location.pathname.startsWith(path),
    )

  if (user && !isComplete && !onProfileGateExemptPath) {
    return (
      <Redirect
        to={{
          pathname: '/admin/profile',
          state: { alert: 'NO_FIRST_LAST_NAME' },
        }}
      />
    )
  }

  return (
    <>
      {status === 'bootstrap_failed' && isAuthRoute && (
        <Box p={2}>
          <Alert severity="warning">
            Session restore failed. Please sign in again.
            {error ? ` (${error})` : ''}
          </Alert>
        </Box>
      )}
      <Switch>
        <Route path="/" exact={true} render={() => <Redirect to="/auth" />} />
        <PublicRoute path="/auth" component={SignIn} />
        <PublicRoute path="/sso" component={DirectSSO} />
        <PublicRoute path="/logout" component={LogoutRedirect} />
        <PublicRoute
          path="/offline-booking/:communicationReference?"
          component={OfflineBookingPage}
        />
        <PrivateRoute path="/admin" component={Admin} />
        <Route path="/booking/create" component={MiniBooking} />
        <Route path="/booking/status/:path" component={Status} />
        <Route
          path="/booking/flexible/:path"
          component={FlexibleBookingControl}
        />
      </Switch>
    </>
  )
}

export default AppRoutes
